import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { getDoc, doc } from "firebase/firestore";
import { firestore } from './firebase'; // Make sure firebase.js exports firestore
import './CustomStyles.css';  // Adjust the path if necessary

const stripePromise = loadStripe('pk_test_51OHYH0BWGJihMgNLb0fTamqmfblH60es8rzShPHgKTH4XBBiikw3PGlJpKDC6LE122i00J1cuAq8QJt8sd1ppSDw00UfqT34pN');

const colors = {
  background: '#FAFAFA',
  primary: '#F0EAE2',
  text: '#333333',
};

const Modal = ({ title, content, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
    <div className="bg-white p-6 rounded-lg max-w-md mx-auto shadow-lg">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      <p className="mb-4">{content}</p>
      <button onClick={onClose} className="bg-[#F0EAE2] text-[#333333] py-2 px-4 rounded-lg hover:bg-[#E0EAE0]">
        Fermer
      </button>
    </div>
  </div>
);

const Register = ({ onToggle }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('annual'); // Default to annual plan
  const [isDiscountActive, setIsDiscountActive] = useState(false);  // For discount status
  const [saleName, setSaleName] = useState('');  // For the sale name

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Track the step (1: registration, 2: choose membership)



  const [showCouponInput, setShowCouponInput] = useState(false); // Toggle coupon input
  const [couponCode, setCouponCode] = useState(''); // Store coupon code
  const [isCouponApplied, setIsCouponApplied] = useState(false); // Track if a valid coupon is applied



  useEffect(() => {
    const fetchDiscountSettings = async () => {
      const docRef = doc(firestore, 'settings', 'discountSettings');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setIsDiscountActive(docSnap.data().isDiscountActive); // Set discount status
        setSaleName(docSnap.data().saleName);  // Set sale name from Firestore
      } else {
        console.log("No such document in Firestore!");
      }
    };

    fetchDiscountSettings();
  }, []);  // Run once when the component mounts

  const validateEmail = (email) => {
    // Regular expression to validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const addToMailchimp = async (email, firstName, lastName, phone) => {
    try {
      const response = await fetch('https://addtomail-p2kswv5j2q-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          phone
        })
      });

      if (response.ok) {
        console.log("User successfully added and tagged in Mailchimp!");
      } else {
        console.error("Failed to add user to Mailchimp:", await response.json());
      }
    } catch (error) {
      console.error("Error adding user to Mailchimp:", error);
    }
  };

  const handleValidateAndProceed = async () => {
    setLoading(true);

    // Check if email format is valid
    if (!validateEmail(email)) {
      alert('Veuillez entrer une adresse e-mail valide avec un @ et un domaine correct.');
      setLoading(false);
      return;
    }

    // Check if email exists
    try {
      const response = await fetch('https://checkemailexists-p2kswv5j2q-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (data.exists) {
        alert("Un compte avec cet e-mail existe déjà. Veuillez utiliser un autre e-mail ou vous connecter.");
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Failed to verify email:', error);
      alert('Échec de la vérification de l\'e-mail. Veuillez réessayer plus tard.');
      setLoading(false);
      return;
    }

    // Coupon validation (only if the checkbox is checked)
    if (showCouponInput && couponCode) {
      try {
        const couponResponse = await fetch('https://api-p2kswv5j2q-uc.a.run.app/validate-coupon', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ coupon: couponCode }),
        });

        const couponData = await couponResponse.json();
        if (!couponData.valid) {
          alert("Code promo invalide. Veuillez réessayer.");
          setLoading(false);
          return; // Exit function if the coupon is invalid
        } else {
          setIsCouponApplied(true); // Set state if the coupon is valid
        }
      } catch (error) {
        console.error("Error validating coupon:", error);
        alert("Échec de la validation du code promo. Veuillez réessayer.");
        setLoading(false);
        return;
      }
    }

    // Password validation
    if (password.length < 6) {
      alert("Le mot de passe doit comporter au moins 6 caractères.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      alert("Les mots de passe ne correspondent pas. Veuillez les saisir à nouveau.");
      setLoading(false);
      return;
    }

    // Phone number validation
    if (phone) {
      let formattedPhone = phone.replace(/[^\d]/g, ''); // Strip non-numeric characters
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;

      if (!formattedPhone || formattedPhone.length < 10 || !phoneRegex.test(formattedPhone)) {
        alert("Veuillez entrer un numéro de téléphone valide avec au moins 10 chiffres.");
        setLoading(false);
        return;
      }
    }

    if (!agreedToTerms) {
      alert("Vous devez accepter les termes et conditions.");
      setLoading(false);
      return;
    }

    // Add to Mailchimp before proceeding
    try {
      await addToMailchimp(email, firstName, lastName, phone);
      console.log('User successfully added to Mailchimp!');
    } catch (error) {
      console.error('Error adding user to Mailchimp:', error);
      alert("Échec de l'ajout à Mailchimp. Veuillez réessayer.");
      setLoading(false);
      return;
    }

    // Proceed to the membership selection page if everything is valid
    setStep(2);
    setLoading(false);
  };



    //this check happens when the user is going to stripe
  const handleRegister = async () => {
    let priceId;
    let couponId = null;

    // The coupon validation has already occurred in `handleValidateAndProceed`.
    // Now just pass the coupon code if it was validated.
    if (showCouponInput && couponCode) {
      couponId = couponCode;  // Pass the valid coupon to Stripe.
    }

    // Check if a discount is active and override the coupon if necessary
    if (isDiscountActive) {
      couponId = 'Discounted_3Months'; // Apply the sale discount
    }

    // Set priceId based on the selected plan
    if (selectedPlan === 'annual') {
      priceId = 'price_1Q7gjDBWGJihMgNLu2kUQTK3';
    } else {
      priceId = 'price_1PgqEkBWGJihMgNLCmGWTU1V';
    }

    try {
      // Proceed with Stripe checkout session
      const response = await fetch('https://api-p2kswv5j2q-uc.a.run.app/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          phone,
          subscriptionPlan: selectedPlan,
          priceId: priceId,
          couponId: couponId, // Pass the coupon code if available
        }),
      });

      const session = await response.json();
      const stripe = await stripePromise;
      const { error } = await stripe.redirectToCheckout({ sessionId: session.id });

      if (error) {
        console.error('Error redirecting to checkout:', error);
      }
    } catch (error) {
      console.error("Error during registration:", error);
      alert("Échec de l’inscription. Veuillez réessayer.");
    }
  };





  const handlePlanSelect = (plan) => setSelectedPlan(plan);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#FAFAFA] p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg">
        {step === 1 ? (
          // First Step: Email Capture and Registration
          <>
            <h2 className="text-2xl font-bold text-center mb-6 text-[#333333]">Rejoins le club Yoga Move Ö Miel</h2>
            <form>
              <input
                className="w-full px-4 py-2 mb-4 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="text"
                placeholder="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              <input
                className="w-full px-4 py-2 mb-4 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="text"
                placeholder="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              <input
                className="w-full px-4 py-2 mb-4 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="tel"
                placeholder="Téléphone (optionnel)"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <input
                className="w-full px-4 py-2 mb-4 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                className="w-full px-4 py-2 mb-4 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="password"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <input
                className="w-full px-4 py-2 mb-6 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                type="password"
                placeholder="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <div className="mb-6">
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    required
                  />
                  <span className="text-sm text-[#333333]">
                    J'accepte les <a href="/terms-and-conditions" target="_blank" className="underline text-blue-600">termes et conditions</a>.
                  </span>
                </label>
              </div>

              <div className="mb-4">
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    onChange={(e) => setShowCouponInput(e.target.checked)} // Toggle coupon input visibility
                  />
                  <span className="text-sm text-[#333333]">J'ai un code promo</span>
                </label>
                {showCouponInput && (
                  <input
                    className="w-full px-4 py-2 mt-2 border border-[#E0EAE0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F0EAE2]"
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)} // Capture coupon code input
                  />
                )}
              </div>


              {/* Select Your Membership Button with loading spinner */}
              <button
                type="button"
                onClick={handleValidateAndProceed}
                className="w-full bg-[#F0EAE2] text-[#333333] py-2 rounded-lg flex items-center justify-center transition-all hover:bg-[#E0EAE0]"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-6 h-6 border-4 border-gray-200 border-t-gray-800 rounded-full animate-spin"></div>
                ) : (
                  <>
                    Sélectionnez Votre Abonnement
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </button>
            </form>
          </>
        ) : (
          // Second Step: Membership Selection
          <>
            {/* Image Section */}
            <div className="w-full mb-6">
              <img 
                src="https://firebasestorage.googleapis.com/v0/b/yogaapp-7dd5d.appspot.com/o/appimages%2Fstack%20v%205.png?alt=media&token=78b48e93-3325-479e-bac9-9d3c375ff672" 
                alt="Abonnement" 
                className="w-full h-25 object-cover rounded-lg"
              />
            </div>

            <h2 className="text-2xl font-bold text-center mb-6 text-[#333333]">Sélectionnez Votre Abonnement</h2>


              {isCouponApplied && (
                <div className="bg-green-100 text-green-600 text-center py-2 px-4 mb-4 rounded-lg">
                  Coupon Applied Successfully!
                </div>
              )}


            {/* Display sale name dynamically with green text and a subtle background */}
            {saleName && (
              <div className="flex justify-center mb-4">
                <div className="bg-white text-green-500 text-lg px-4 py-2 rounded-lg shadow-md">
                  <span className="font-bold">{saleName} Réduction</span>, Fin Proche !
                </div>
              </div>
            )}

            {/* Subscription Plan Selection */}
            <div className="flex justify-between mb-6">
              <div
                className={`border-2 p-4 w-48 rounded-lg cursor-pointer ${selectedPlan === 'monthly' ? 'border-blue-500' : 'border-gray-300'}`}
                onClick={() => handlePlanSelect('monthly')}
              >
                <h3 className="font-bold text-center">Mensuel</h3>
                <p className="text-center">$45/mois</p>
                {/* Show discount under monthly plan if active */}
                {isDiscountActive && (
                  <p className="text-center text-green-500">Réduction Appliquée: 29,99€ pour les 3 premiers mois</p>
                )}
              </div>
              <div
                className={`relative border-2 p-4 w-48 rounded-lg cursor-pointer ${selectedPlan === 'annual' ? 'border-blue-500' : 'border-gray-300'}`}
                onClick={() => handlePlanSelect('annual')}
              >
                <div className="absolute top-0 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded-br-lg">Le Plus Populaire</div>
                <h3 className="font-bold text-center">Annuel</h3>
                <p className="text-center">$420/an</p>
                <p className="text-center text-sm text-gray-500">Économisez 120€</p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleRegister}
              className="w-full bg-[#F0EAE2] text-[#333333] py-2 rounded-lg flex items-center justify-center transition-all hover:bg-[#E0EAE0]"
            >
              S’inscrire
              <ArrowRight className="ml-2" size={18} />
            </button>
          </>
        )}

        <button onClick={onToggle} className="mt-4 text-sm text-[#333333]">
          Vous avez déjà un compte ? Connexion
        </button>
      </div>

      {/* Modals */}
      {showTerms && <Modal title="Termes et Conditions" content="Texte temporaire des termes et conditions." onClose={() => setShowTerms(false)} />}
      {showDisclaimers && <Modal title="Avertissements" content="Texte temporaire des avertissements." onClose={() => setShowDisclaimers(false)} />}
    </div>
  );
};

export default Register;
